// frontend/src/components/Chatbot/ChatInputBar.tsx
// Presentational component for the chat input textarea, send button, and disclaimer.

import {
  forwardRef,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type FormEventHandler,
} from 'react';
import { Send } from 'lucide-react';

export interface ChatInputBarProps {
  input: string;
  onInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: FormEvent | KeyboardEvent<HTMLTextAreaElement>) => void;
  isLoading: boolean;
  isConfirming: boolean;
}

const ChatInputBar = forwardRef<HTMLTextAreaElement, ChatInputBarProps>(
  ({ input, onInputChange, onSubmit, isLoading, isConfirming }, ref) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit(e);
      }
    };

    const handleInput = (e: FormEvent<HTMLTextAreaElement>): void => {
      const target = e.target as HTMLTextAreaElement;
      target.style.height = 'auto';
      target.style.height = Math.min(target.scrollHeight, 200) + 'px';
    };

    const handleFormSubmit: FormEventHandler<HTMLFormElement> = (e) => {
      onSubmit(e);
    };

    return (
      <form onSubmit={handleFormSubmit}>
        <div className="flex space-x-2 items-end">
          <textarea
            ref={ref}
            value={input}
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            aria-label="Chat message input"
            placeholder="Ask about courses or get help... (Shift+Enter for new line)"
            className="flex-1 px-3 py-2.5 text-sm border border-line-soft rounded-control bg-surface-card focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] max-h-[200px] resize-none"
            disabled={isLoading || isConfirming}
            rows={1}
            style={{
              height: 'auto',
              minHeight: '44px',
              maxHeight: '200px',
              overflowY: input.split('\n').length > 5 ? 'auto' : 'hidden',
            }}
            onInput={handleInput}
          />
          <button
            type="submit"
            disabled={isLoading || isConfirming || !input.trim()}
            className="px-4 py-2.5 text-white rounded-control disabled:opacity-50 disabled:cursor-not-allowed text-sm bg-primary hover:bg-primary-800 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-2 text-xs text-ink-3 text-right hidden sm:block">
          <div>The AI Assistant can make mistakes.</div>
          <div>Always consult with your academic advisor.</div>
        </div>
      </form>
    );
  }
);

ChatInputBar.displayName = 'ChatInputBar';

export default ChatInputBar;
